import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  ArrowLeft,
  RefreshCw,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Inbox,
  CalendarDays,
  DollarSign,
  Mail,
  Phone,
  X,
  Bot,
  LogOut,
} from "lucide-react";
import { useAuth, authHeaders } from "@/context/AuthContext";
import SyncBadge from "@/components/admin/SyncBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending_review: { label: "Pending review", color: "#a395c3", icon: Clock },
  vetting: { label: "Vetting", color: "#00f0ff", icon: Bot },
  approved: { label: "Approved", color: "#22c55e", icon: CheckCircle2 },
  declined: { label: "Declined", color: "#ff0055", icon: XCircle },
  needs_info: { label: "Needs info", color: "#ffb800", icon: AlertTriangle },
  booked: { label: "Booked", color: "#a78bfa", icon: CheckCircle2 },
};

const TABS = [
  "all",
  "pending_review",
  "vetting",
  "approved",
  "needs_info",
  "declined",
  "booked",
];

const monday_board_url = "https://sbmediastudio.monday.com/boards/18418415830";

const AdminBookings = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending_review");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [selected, setSelected] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = tab === "all" ? {} : { status_filter: tab };
      const [list, st] = await Promise.all([
        axios.get(`${API}/bookings`, {
          headers: authHeaders(token),
          params,
        }),
        axios.get(`${API}/bookings/stats`, { headers: authHeaders(token) }),
      ]);
      setItems(list.data);
      setStats(st.data);
    } catch {
      toast.error("Couldn't load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [tab]);

  const decide = async (id, status) => {
    setActing(id + status);
    try {
      const { data } = await axios.patch(
        `${API}/bookings/${id}`,
        { status },
        { headers: authHeaders(token) },
      );
      toast.success(`Booking marked ${STATUS_META[status].label}. Synced to Monday.`);
      setItems((prev) =>
        tab === "all" || tab === status
          ? prev.map((p) => (p.id === id ? data : p))
          : prev.filter((p) => p.id !== id),
      );
      fetchAll();
      setSelected(null);
    } catch {
      toast.error("Action failed.");
    } finally {
      setActing(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#05050a] text-white" data-testid="admin-bookings">
      <header className="border-b border-white/10 bg-[#05050a]/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Link
              to="/"
              data-testid="admin-bookings-back-home"
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white flex-shrink-0"
            >
              <ArrowLeft size={14} />
              <span className="hidden md:inline">Back to site</span>
            </Link>
            <span className="hidden md:block h-5 w-px bg-white/15" />
            <div className="min-w-0">
              <div className="font-display font-black text-sm tracking-tight truncate">
                SB Admin · Bookings
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] truncate">
                {user?.email}
              </div>
            </div>
            <span className="hidden md:block h-5 w-px bg-white/15" />
            <Link
              to="/admin"
              data-testid="admin-bookings-to-guests"
              className="hidden md:inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#00f0ff]"
            >
              Guest requests →
            </Link>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={monday_board_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-bookings-monday-link"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-xs hover:bg-white/10"
            >
              <Bot size={13} /> Monday board <ExternalLink size={11} />
            </a>
            <button
              onClick={fetchAll}
              data-testid="admin-bookings-refresh"
              aria-label="Refresh"
              className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-bookings-logout"
              aria-label="Logout"
              title="Logout"
              className="w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2 inline-flex items-center justify-center gap-2 rounded-full bg-white/5 border border-white/10 text-xs hover:bg-white/10"
            >
              <LogOut size={14} className="md:hidden" />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          {[
            "pending_review",
            "vetting",
            "approved",
            "needs_info",
            "declined",
            "booked",
          ].map((k) => {
            const m = STATUS_META[k];
            return (
              <div
                key={k}
                data-testid={`admin-bookings-stat-${k}`}
                className="rounded-2xl bg-white/[0.03] border border-white/10 p-4"
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-[#a395c3]">
                  <m.icon size={11} /> {m.label}
                </div>
                <div
                  className="font-display font-black text-2xl mt-1.5"
                  style={{ color: m.color }}
                >
                  {stats[k] ?? 0}
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {TABS.map((t) => {
            const active = tab === t;
            const m = STATUS_META[t];
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`admin-bookings-tab-${t}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  active
                    ? "bg-white text-[#05050a]"
                    : "bg-white/[0.04] border border-white/10 text-white/70 hover:text-white"
                }`}
              >
                {m ? <m.icon size={12} /> : <Inbox size={12} />}
                {t === "all" ? "All" : m.label}
                {t !== "all" && (
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      active ? "bg-[#05050a] text-white" : "bg-white/10"
                    }`}
                  >
                    {stats[t] ?? 0}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="admin-bookings-list">
          {loading ? (
            <div className="p-16 flex items-center justify-center text-white/40">
              <Loader2 className="animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center">
              <Inbox className="mx-auto w-10 h-10 text-white/30" />
              <div className="font-display text-lg mt-4 text-white/70">
                No bookings here yet
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((b) => {
                const m = STATUS_META[b.status] || STATUS_META.pending_review;
                return (
                  <li
                    key={b.id}
                    data-testid={`admin-booking-${b.id}`}
                    className="p-5 md:p-6 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-display font-bold text-lg truncate">
                            {b.name}
                          </span>
                          <StatusPill status={b.status} />
                          {b.monday_synced ? (
                            <span className="inline-flex items-center gap-1 text-[10px] text-[#a78bfa]">
                              <Bot size={10} /> Monday synced
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] text-[#ffb800]">
                              <AlertTriangle size={10} /> Monday sync failed
                            </span>
                          )}
                        </div>
                        <div className="mt-1.5 text-sm text-[#a395c3] flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="inline-flex items-center gap-1.5">
                            <Mail size={12} /> {b.email}
                          </span>
                          {b.preferred_date && (
                            <span className="inline-flex items-center gap-1.5">
                              <CalendarDays size={12} /> {b.preferred_date}
                              {b.preferred_time ? ` · ${b.preferred_time}` : ""}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1.5">
                            {b.package_label || b.package}
                          </span>
                          {b.budget > 0 && (
                            <span className="inline-flex items-center gap-1.5">
                              <DollarSign size={12} /> {b.budget}
                            </span>
                          )}
                        </div>
                        {b.ai_verdict && (
                          <div className="mt-2 text-xs text-white/70 line-clamp-1 italic">
                            <Bot size={10} className="inline mr-1 text-[#00f0ff]" />
                            {b.ai_verdict}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => setSelected(b)}
                          data-testid={`admin-booking-view-${b.id}`}
                          className="px-3 py-2 rounded-full bg-white/[0.04] border border-white/10 text-xs hover:bg-white/10"
                        >
                          View
                        </button>
                        {b.status !== "approved" && b.status !== "booked" && (
                          <button
                            onClick={() => decide(b.id, "approved")}
                            disabled={acting === b.id + "approved"}
                            data-testid={`admin-booking-approve-${b.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/30 text-[#22c55e] text-xs hover:bg-[#22c55e] hover:text-[#05050a] transition-colors disabled:opacity-50"
                          >
                            {acting === b.id + "approved" ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <CheckCircle2 size={13} />
                            )}
                            Approve
                          </button>
                        )}
                        {b.status !== "declined" && (
                          <button
                            onClick={() => decide(b.id, "declined")}
                            disabled={acting === b.id + "declined"}
                            data-testid={`admin-booking-decline-${b.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#ff0055]/15 border border-[#ff0055]/30 text-[#ff0055] text-xs hover:bg-[#ff0055] hover:text-white transition-colors disabled:opacity-50"
                          >
                            {acting === b.id + "declined" ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <XCircle size={13} />
                            )}
                            Decline
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="mt-2.5 text-[11px] text-white/40 flex items-center gap-2 flex-wrap">
                      <span>Submitted {new Date(b.created_at).toLocaleString()}</span>
                      {b.monday_item_id && (
                        <>
                          <span>·</span>
                          <a
                            href={`${monday_board_url}/pulses/${b.monday_item_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-[#a78bfa]"
                          >
                            Monday item #{b.monday_item_id}
                          </a>
                        </>
                      )}
                      {b.source_of_truth && b.last_synced_at && (
                        <SyncBadge source={b.source_of_truth} syncedAt={b.last_synced_at} />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>

      {selected && (
        <BookingDetailModal
          item={selected}
          onClose={() => setSelected(null)}
          onAction={(s) => decide(selected.id, s)}
          acting={acting}
        />
      )}
    </div>
  );
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending_review;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold"
      style={{
        color: m.color,
        background: `${m.color}15`,
        border: `1px solid ${m.color}40`,
      }}
    >
      <span className="w-1 h-1 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
};

const BookingDetailModal = ({ item, onClose, onAction, acting }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    onClick={onClose}
    className="fixed inset-0 z-[70] overflow-y-auto overscroll-contain bg-black/80 backdrop-blur-md"
    data-testid="admin-booking-detail-modal"
  >
    <div className="min-h-full flex items-start justify-center px-3 py-6 sm:py-10">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl my-4 rounded-3xl bg-[#0a0612] border border-white/10"
      >
      <div className="sticky top-0 flex items-start justify-between gap-4 px-7 pt-7 pb-5 bg-gradient-to-b from-[#0a0612] to-transparent">
        <div>
          <StatusPill status={item.status} />
          <h3 className="font-display font-black text-2xl mt-2.5 tracking-tight">
            {item.name}
          </h3>
          <div className="text-[#a395c3] text-sm">{item.package_label}</div>
        </div>
        <button
          onClick={onClose}
          data-testid="admin-booking-detail-close"
          className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10"
        >
          <X size={16} />
        </button>
      </div>

      <div className="px-7 pb-7 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DetailRow icon={Mail} label="Email" value={item.email} />
          <DetailRow icon={Phone} label="Phone" value={item.phone || "—"} />
          <DetailRow
            icon={CalendarDays}
            label="Preferred date"
            value={
              item.preferred_date
                ? `${item.preferred_date}${item.preferred_time ? " · " + item.preferred_time : ""}`
                : "—"
            }
          />
          <DetailRow
            icon={CalendarDays}
            label="Alt date"
            value={item.alt_date || "—"}
          />
          <DetailRow
            icon={DollarSign}
            label="Budget"
            value={item.budget > 0 ? `$${item.budget}` : "—"}
          />
          <DetailRow
            icon={Clock}
            label="Submitted"
            value={new Date(item.created_at).toLocaleString()}
          />
        </div>

        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2">
            Project brief
          </div>
          <div className="text-sm whitespace-pre-line text-white/90">
            {item.project_brief}
          </div>
        </div>

        {item.social_links && (
          <DetailRow
            icon={ExternalLink}
            label="Socials"
            value={item.social_links}
          />
        )}

        {item.ai_verdict && (
          <div className="rounded-2xl bg-[#00f0ff]/5 border border-[#00f0ff]/25 p-5">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#00f0ff] mb-2 flex items-center gap-1.5">
              <Bot size={11} /> Concierge verdict
            </div>
            <div className="text-sm text-white/90">{item.ai_verdict}</div>
            {item.calendar_conflict && item.calendar_conflict !== "unknown" && (
              <div className="text-[11px] text-[#a395c3] mt-2">
                Calendar conflict: <span className="text-white">{item.calendar_conflict}</span>
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          {item.status !== "approved" && item.status !== "booked" && (
            <button
              onClick={() => onAction("approved")}
              disabled={acting === item.id + "approved"}
              data-testid="admin-booking-detail-approve"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#22c55e] text-[#05050a] text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              <CheckCircle2 size={14} /> Approve
            </button>
          )}
          {item.status !== "booked" && item.status === "approved" && (
            <button
              onClick={() => onAction("booked")}
              disabled={acting === item.id + "booked"}
              data-testid="admin-booking-detail-booked"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#a78bfa] text-[#05050a] text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              <CheckCircle2 size={14} /> Mark as booked
            </button>
          )}
          {item.status !== "needs_info" && (
            <button
              onClick={() => onAction("needs_info")}
              disabled={acting === item.id + "needs_info"}
              data-testid="admin-booking-detail-needs-info"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#ffb800]/15 border border-[#ffb800]/40 text-[#ffb800] text-sm font-semibold hover:bg-[#ffb800] hover:text-[#05050a] disabled:opacity-50"
            >
              <AlertTriangle size={14} /> Needs info
            </button>
          )}
          {item.status !== "declined" && (
            <button
              onClick={() => onAction("declined")}
              disabled={acting === item.id + "declined"}
              data-testid="admin-booking-detail-decline"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#ff0055] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              <XCircle size={14} /> Decline
            </button>
          )}
          <a
            href={`mailto:${item.email}?subject=Your%20SB%20Media%20booking`}
            data-testid="admin-booking-detail-email"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm hover:bg-white/10"
          >
            <Mail size={14} /> Email
          </a>
          {item.monday_item_id && (
            <a
              href={`${monday_board_url}/pulses/${item.monday_item_id}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-booking-detail-monday"
              className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm text-[#a78bfa] hover:bg-[#a78bfa]/10"
            >
              <Bot size={14} /> Open in Monday <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </motion.div>
    </div>
  </motion.div>
);

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
      <Icon size={13} className="text-[#00f0ff]" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#a395c3]">
        {label}
      </div>
      <div className="text-sm text-white mt-0.5 break-words">{value}</div>
    </div>
  </div>
);

export default AdminBookings;
